import { getOperatorLabel } from '../constants/violationConstants';

/**
 * Предложный падеж от GENERAL_ZONE_LABEL («Общая область контроля»).
 * Склонять программно нечем, поэтому форма задана явно — она должна
 * меняться вместе с константой в constants/violationConstants.js.
 */
const GENERAL_ZONE_PHRASE = 'в общей области контроля';

const DASH = '—';
const UNKNOWN_METRIC_LABEL = 'Показатель';

/**
 * FE-FIX03. Единственный presentation-utility для условий правил и событий.
 *
 * Источник подписи метрики — каталог камеры (GET /admin/violation-rule-catalog),
 * затем обязательная статическая карта ТЗ §6.3, затем технический ID. Каталог
 * привязан к камере и не содержит подписей операторов и признаков, поэтому они
 * живут только здесь. Transport-ID в payload не переименовываются — этот модуль
 * работает исключительно на отображение.
 */
const METRIC_LABELS = {
  people_count: 'Количество людей',
  people: 'Люди',
  gloves: 'Перчатки',
  masks: 'Маски',
  headwear: 'Головные уборы',
  hats: 'Головные уборы',
  people_without_gloves: 'Людей без перчаток',
  people_without_masks: 'Людей без маски',
  people_without_hats: 'Людей без головного убора',
};

const FEATURE_LABELS = {
  people: 'Люди',
  // people_legacy — внутреннее транспортное имя детекции людей, пользователю
  // его показывать нельзя (та же подмена, что в constants/analyticsConstants).
  people_legacy: 'Люди',
  gloves: 'Перчатки',
  masks: 'Маски',
  headwear: 'Головные уборы',
};

export const metricLabel = (metric, catalog) => {
  if (!metric) return DASH;
  const fromCatalog = (catalog?.metrics || []).find((item) => item?.metric === metric);
  if (fromCatalog?.display_name) return fromCatalog.display_name;
  return Object.hasOwn(METRIC_LABELS, metric) ? METRIC_LABELS[metric] : UNKNOWN_METRIC_LABEL;
};

/**
 * Срез распределения: label бэкенда, если он человекочитаемый (не равен category),
 * иначе подпись метрики по category.
 */
export const distributionLabel = (slice) => {
  if (!slice) return DASH;
  const { category, label } = slice;
  if (label && label !== category) return label;
  // label ≠ category -> label; иначе известная метрика -> карта; иначе label как есть (у срезов-правил label === category).
  if (Object.hasOwn(METRIC_LABELS, category)) return METRIC_LABELS[category];
  return label || category || DASH;
};

export const featureLabel = (featureId) => {
  if (!featureId) return '';
  return Object.hasOwn(FEATURE_LABELS, featureId) ? FEATURE_LABELS[featureId] : String(featureId);
};

/** ТЗ §6.4: несколько признаков — через запятую; нет признаков — строки нет вовсе. */
export const featuresLabel = (featureIds) =>
  (Array.isArray(featureIds) ? featureIds : [])
    .map(featureLabel)
    .filter(Boolean)
    .join(', ');

export const operatorLabel = getOperatorLabel;

/**
 * threshold_json — JSON-скаляр строкой. Битое значение показываем как есть,
 * объект/массив считаем отсутствующим порогом: карточка не должна падать
 * и не должна печатать `[object Object]`.
 */
export const parseThreshold = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw !== 'string') return typeof raw === 'object' ? null : raw;

  try {
    const parsed = JSON.parse(raw);
    if (parsed !== null && typeof parsed === 'object') return null;
    return parsed;
  } catch (error) {
    return raw;
  }
};

export const formatThresholdValue = (value) => {
  if (value === true) return 'Да';
  if (value === false) return 'Нет';
  if (value === null || value === undefined || value === '') return '';
  return String(value);
};

const readThreshold = (source) =>
  parseThreshold(source?.threshold_json ?? source?.threshold);

/** «Количество людей ≥ 2» — ТЗ §6.4. */
export const formatRuleCondition = (source, { catalog } = {}) => {
  if (!source || typeof source !== 'object') return DASH;
  if (!source.metric) return DASH;

  const parts = [metricLabel(source.metric, catalog)];
  const threshold = formatThresholdValue(readThreshold(source));

  if (source.operator && threshold !== '') parts.push(`${operatorLabel(source.operator)} ${threshold}`);
  else if (threshold !== '') parts.push(threshold);

  return parts.join(' ');
};

/**
 * ТЗ §6.5. Строится строго из event.rule_snapshot — событие остаётся
 * исторически корректным, даже если правило позже изменили. Имя зоны
 * приходит снаружи: резолвер зон знает про камеру, а этот модуль — нет.
 */
export const formatEventCondition = (event, { zoneName, catalog } = {}) => {
  const snapshot = event?.rule_snapshot;
  const condition = formatRuleCondition(snapshot, { catalog });
  if (condition === DASH) return DASH;

  const parts = [condition];

  if (snapshot.zone_id === null || snapshot.zone_id === undefined || snapshot.zone_id === '') {
    parts.push(GENERAL_ZONE_PHRASE);
  } else if (zoneName) {
    parts.push(`в зоне «${zoneName}»`);
  }

  const duration = Number(snapshot.duration_seconds);
  if (Number.isFinite(duration) && duration > 0) parts.push(`на протяжении ${duration} секунд`);

  return parts.join(' ');
};
